import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEOHead from '../Components/SEOHead';
import PageBreadcrumbs from '../Components/PageBreadcrumbs';
import WavyLine from '../Components/wavyline';
import LunularLaserImage from '../pics/lunula-laser-fungal-treatment-manchester-salford.png';
import { 
  FaCheckCircle, 
  FaArrowRight, 
  FaPhone
} from 'react-icons/fa';

function LunularLaserPage() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  // Multiple parallax transforms for layered effect
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -400]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const y4 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const opacity1 = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [1, 0.8, 0.6, 0.4]);
  const scale1 = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.1, 1.2]);
  
  // Smooth spring animations
  const springY1 = useSpring(y1, { stiffness: 100, damping: 30 });
  const springY2 = useSpring(y2, { stiffness: 100, damping: 30 });
  const springY3 = useSpring(y3, { stiffness: 100, damping: 30 });
  const springY4 = useSpring(y4, { stiffness: 100, damping: 30 });
  const springOpacity = useSpring(opacity1, { stiffness: 100, damping: 30 });
  const springScale = useSpring(scale1, { stiffness: 50, damping: 20 });

  // Scroll progress indicator
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // In-view animations for sections
  const heroRef = useRef(null);
  const benefitsRef = useRef(null);
  const processRef = useRef(null);
  
  const heroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const benefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const processInView = useInView(processRef, { once: true, margin: "-100px" });

  // SEO structured data
  const lunulaLaserStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Lunula Laser Treatment Manchester - Drosh The Pod",
    "description": "Revolutionary painless Lunula Laser treatment in Manchester. Grow new perfect nails with our advanced laser technology. Premium podiatry service for nail regeneration.",
    "url": "https://www.droshpod.com/lunular-laser",
    "provider": {
      "@type": "Organization",
      "name": "Drosh The Pod",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Manchester",
        "addressRegion": "Greater Manchester",
        "addressCountry": "GB"
      }
    },
    "serviceType": "Lunula Laser Treatment",
    "areaServed": "Manchester",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    }
  };

  const benefits = [
    "Completely painless",
    "Boosts nail growth",
    "No recovery time",
    "Treats fungal infections",
    "Personalised assessment",
    "Long-lasting results"
  ];

  return (
    <>
      <SEOHead 
        title="Lunula Laser Treatment Manchester | Painless Nail Regeneration"
        description="Revolutionary Lunula Laser treatment in Manchester. Completely painless procedure that enables you to grow new perfect nails. Premium podiatry service using advanced laser technology. Book your consultation today."
        keywords="lunula laser manchester, laser nail treatment, painless nail treatment, nail regeneration, laser podiatry manchester, perfect nails, nail growth treatment, advanced laser therapy, manchester podiatry clinic"
        canonical="https://www.droshpod.com/lunular-laser"
        structuredData={lunulaLaserStructuredData}
      />
      
      <main ref={containerRef} className="relative overflow-hidden min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        {/* Scroll Progress Bar */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary via-primary to-secondary origin-left z-50"
          style={{ scaleX }}
        />

        {/* Multi-layer Parallax Background Elements */}
        <motion.div 
          className="fixed inset-0 pointer-events-none z-0"
          style={{ y: springY1 }}
        >
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary/15 rounded-full blur-2xl"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-secondary/15 rounded-full blur-2xl"></div>
          <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-purple-300/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-1/3 w-28 h-28 bg-pink-300/10 rounded-full blur-2xl"></div>
        </motion.div>

        <motion.div 
          className="absolute inset-0 pointer-events-none z-0"
          style={{ y: springY2 }}
        >
          <div className="absolute top-1/4 right-10 w-40 h-40 border-2 border-primary/20 rounded-full"></div>
          <div className="absolute bottom-1/3 left-10 w-32 h-32 border-2 border-secondary/20 rounded-full"></div>
          <div className="absolute top-2/3 right-1/4 w-24 h-24 border border-purple-300/20 rounded-full"></div>
        </motion.div>

        <motion.div 
          className="absolute inset-0 pointer-events-none z-0"
          style={{ y: springY3, opacity: springOpacity }}
        >
          <div className="absolute top-1/3 left-1/4 w-36 h-36 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-1/4 right-1/3 w-28 h-28 bg-gradient-to-br from-purple-300/10 to-pink-300/10 rounded-full blur-xl"></div>
        </motion.div>

        {/* Wavy Background with Parallax */}
        <motion.div 
          className="absolute inset-0 top-1/2 overflow-hidden -z-10"
          style={{ y: springY4 }}
        >
          <WavyLine />
        </motion.div>

        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 pt-1 pb-1 relative z-10">
          <PageBreadcrumbs />
        </div>

        {/* Hero Section with Enhanced Parallax */}
        <section ref={heroRef} className="relative z-10 py-2 md:py-4">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={heroInView ? { x: 0, opacity: 1 } : { x: -100, opacity: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="order-2 lg:order-1"
              >
                <motion.div
                  animate={{ 
                    y: [0, -10, 0],
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="inline-block mb-4"
                >
              
                </motion.div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 leading-tight">
                  Lunula Laser
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">
                    Treatment
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-gray-700 mb-2 font-semibold">
                  Grow New Perfect Nails
                </p>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                  Painless laser treatment that stimulates natural nail growth. Available exclusively at our Manchester clinic.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 mt-2">
                  <Link 
                    to="/consultation-form"
                    className="bg-gradient-to-r from-secondary to-primary text-white px-6 py-3 rounded-full font-semibold hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center transform hover:scale-105 text-sm md:text-base"
                  >
                    Book Your Consultation
                    <FaArrowRight className="ml-2" />
                  </Link>
                  <a 
                    href="tel:07809267586"
                    className="border-2 border-secondary text-secondary px-6 py-3 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300 inline-flex items-center justify-center text-sm md:text-base"
                  >
                    <FaPhone className="mr-2" />
                    Call 07809 267586
                  </a>
                </div>
              </motion.div>
              
              {/* Stacked elements in same position */}
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                animate={heroInView ? { x: 0, opacity: 1 } : { x: 100, opacity: 0 }}
                transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                className="order-1 lg:order-2 relative"
              >
                <div className="relative w-full h-[500px] flex items-center justify-center">
                  {/* Device image - Bottom layer (z-10) */}
                  <motion.div
                    style={{ scale: springScale }}
                    className="absolute inset-0 flex items-center justify-center z-10"
                  >
                    <img 
                      src={LunularLaserImage} 
                      alt="Lunula Laser by Erchonia - Advanced nail regeneration treatment device" 
                      className="h-auto object-contain max-h-[500px]"
                    />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-12 md:py-16 relative z-10 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">Treatment Pricing</h2>
                <p className="text-gray-600">Choose the package that works best for you</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Single Session */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6 hover:shadow-xl transition-shadow"
                >
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">1 Session</h3>
                    <p className="text-sm text-gray-500">Single treatment</p>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">One foot or hand</span>
                      <span className="text-2xl font-bold text-secondary">£115</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">Two feet or hands</span>
                      <span className="text-2xl font-bold text-secondary">£145</span>
                    </div>
                  </div>
                </motion.div>

                {/* 4 Sessions Package */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-xl shadow-lg border-2 border-secondary/40 p-6 hover:shadow-xl transition-shadow relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">4 Sessions</h3>
                    <p className="text-sm text-gray-500">Package deal</p>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">One foot or hand</span>
                      <span className="text-2xl font-bold text-secondary">£400</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">Two feet or hands</span>
                      <span className="text-2xl font-bold text-secondary">£500</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-secondary/30">
                    <p className="text-xs text-gray-600 text-center">
                      <FaCheckCircle className="inline text-green-500 mr-1" />
                      Includes anti-fungal spray
                    </p>
                  </div>
                </motion.div>

                {/* 10 Sessions Package */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl shadow-lg border-2 border-primary/40 p-6 hover:shadow-xl transition-shadow relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    BEST VALUE
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">10 Sessions</h3>
                    <p className="text-sm text-gray-500">Complete package</p>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">One foot or hand</span>
                      <span className="text-2xl font-bold text-secondary">£900</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">Two feet or hands</span>
                      <span className="text-2xl font-bold text-secondary">£1,000</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-primary/30">
                    <p className="text-xs text-gray-600 text-center">
                      <FaCheckCircle className="inline text-green-500 mr-1" />
                      Includes anti-fungal spray
                    </p>
                  </div>
                </motion.div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600">
                  *All treatments include fungal nail debridement. Laser treatment per session. Initial consultation required.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section ref={benefitsRef} className="py-16 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white relative z-10">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                What Makes This Different?
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                Tried other treatments? Here's why this works differently.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-green-500 text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">Completely Painless</h3>
                <p className="text-gray-600 text-sm">No discomfort during treatment. Quick sessions that fit into your day.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-primary text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">Boosts Nail Growth</h3>
                <p className="text-gray-600 text-sm">Stimulates blood flow and cellular activity to promote faster, healthier nail regeneration.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500 text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">No Recovery Time</h3>
                <p className="text-gray-600 text-sm">Walk in, walk out. Return to your normal activities immediately.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500 text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">Treats Fungal Infections</h3>
                <p className="text-gray-600 text-sm">Antimicrobial violet light targets fungal organisms directly at the nail bed.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-secondary text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">Personalised Assessment</h3>
                <p className="text-gray-600 text-sm">We evaluate your specific needs before recommending treatment.</p>
              </motion.div>

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={benefitsInView ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-yellow-500 text-center"
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">Long-Lasting Results</h3>
                <p className="text-gray-600 text-sm">Comprehensive treatment approach for sustained improvement.</p>
              </motion.div>

            </div>
          </div>
        </section>

        {/* How It Works Section - Real Facts About Lunula Laser */}
        <section ref={processRef} className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white relative z-10 overflow-hidden">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={processInView ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                How It Works
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                The science behind the treatment
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <motion.div
                initial={{ x: -100, opacity: 0, scale: 0.9 }}
                whileInView={{ x: 0, opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-violet-600 relative overflow-hidden group"
              >
                <motion.div
                  className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                <div className="relative z-10 opacity-100">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Violet Light (405nm)</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Antimicrobial properties target fungal organisms directly. Penetrates the nail plate to reach the infection at the nail bed without damaging healthy tissue.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ x: 100, opacity: 0, scale: 0.9 }}
                whileInView={{ x: 0, opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, type: "spring", stiffness: 100, delay: 0.05 }}
                viewport={{ once: true, margin: "-100px" }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-red-500 relative overflow-hidden group"
              >
                <motion.div
                  className="absolute top-0 left-0 w-32 h-32 bg-red-500/10 rounded-full blur-2xl -translate-y-1/2 -translate-x-1/2"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 0.25 }}
                />
                <div className="relative z-10 opacity-100">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Red Light (635nm)</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Stimulates blood flow and cellular activity in the nail matrix. Increased circulation brings more nutrients and oxygen, promoting faster and healthier nail growth.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Video Section */}
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="mt-16 max-w-4xl mx-auto"
            >
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-2xl"
                  src="https://www.youtube.com/embed/H65nERMwHMc?mute=1"
                  title="How Lunula Laser Works | Leading Laser for Fungal Nail Treatment | Erchonia Lasers"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              <p className="text-xs text-gray-500 text-center mt-4 italic">
                Video property of Erchonia Corporation. This video is not the property of Drosh The Pod Ltd.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-secondary via-primary to-secondary text-white relative z-10 overflow-hidden">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ 
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 bg-white/10 rounded-full blur-3xl"
            style={{ y: springY1 }}
          ></motion.div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Think This Might Work For You?
              </h2>
              <p className="text-xl md:text-2xl mb-4 opacity-95">
                The first step is a proper assessment
              </p>
              <p className="text-lg md:text-xl mb-10 opacity-90 max-w-2xl mx-auto">
                We'll have a look at your nails, discuss your history, and be straight with you about whether Lunula Laser is the right approach. No hard sell, just honest advice. Based in Manchester, serving clients across Greater Manchester.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/consultation-form"
                  className="bg-white text-secondary px-10 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center transform hover:scale-105 shadow-lg"
                >
                  Book Your Consultation
                  <FaArrowRight className="ml-2" />
                </Link>
                <a 
                  href="tel:07809267586"
                  className="border-2 border-white text-white px-10 py-4 rounded-full font-semibold hover:bg-white hover:text-secondary transition-all duration-300 inline-flex items-center justify-center transform hover:scale-105"
                >
                  <FaPhone className="mr-2" />
                  Call 07809 267586
                </a>
              </div>
              <p className="mt-8 text-sm opacity-80">
                Premium treatment available for Manchester-based clients
              </p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}

export default LunularLaserPage;

